import Raven from 'raven-js';
import * as ActionTypes from 'MeetingsLib/redux/actions/ActionTypes';
import { getTrackingParamsAttribution, getUrlAttributionParams } from 'MeetingsLib/utils/trackingAttribution';
import { getMeetingSourceFromLocation } from 'MeetingsLib/utils/utils';
import { getSlug } from 'MeetingsLib/selectors/RESTParamSelectors';
import { track } from 'MeetingsLib/tracking/UsageTracker';
const getFilledFormFields = formState => {
  const filledFields = [];
  const hasValue = value => {
    if (typeof value !== 'number' && !value) {
      return false;
    }
    // Handle array values (like checkboxes)
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    // Handle string values
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return true;
  };

  // Fields to exclude from tracking (internal/system fields)
  const excludeFields = ['troubleshoot', 'uuid'];

  // Return empty array if formState is null or undefined
  if (!formState) {
    return filledFields;
  }

  // Check standard form fields (email, firstName, lastName)
  const standardFields = ['email', 'firstName', 'lastName'];
  standardFields.forEach(fieldName => {
    if (!excludeFields.includes(fieldName)) {
      const value = formState[fieldName];
      if (hasValue(value)) {
        filledFields.push(fieldName);
      }
    }
  });

  // Check custom form fields (ImmutableMap)
  const customFormFields = formState.customFormFields;
  if (customFormFields && customFormFields.entrySeq) {
    customFormFields.entrySeq().forEach(([customFieldName, customFieldValue]) => {
      if (!excludeFields.includes(customFieldName) && hasValue(customFieldValue)) {
        filledFields.push(customFieldName);
      }
    });
  }
  return filledFields;
};
export default (({
  getState
}) => next => action => {
  next(action);
  const state = getState();
  const meetingSource = getMeetingSourceFromLocation();
  const trackingParams = state.trackingParams ? state.trackingParams.compact() : {};
  switch (action.type) {
    case ActionTypes.LOG_MEETING_CREATE:
      {
        const allFormFields = getFilledFormFields(state.formState);
        track('Meetings', 'Scheduling Page Usage', Object.assign({
          action: 'booked meeting',
          referrer: trackingParams['referrer'] === 'FORMS' ? 'forms' : 'none',
          hasCustomProperties: allFormFields && allFormFields.length
        }, getTrackingParamsAttribution(trackingParams), getUrlAttributionParams({
          slug: getSlug(state)
        })));

        // Fire google tag manager event manually on successful meeting booked
        // do not remove, marketing ops is using this event to track pqls
        try {
          // <!-- Convert Experiments conversion tracking -->
          const _conv_q = window._conv_q || [];
          _conv_q.push(['triggerConversion', '10038692']);
          const gtmMeetingBookedEvent = {
            event: 'MeetingBookedEvent',
            MeetingBookedText: 'Meeting Booked'
          };
          if (window.dataLayer && window.google_tag_manager) {
            window.dataLayer.push(gtmMeetingBookedEvent);
          }
        } catch (e) {
          Raven.captureBreadcrumb({
            message: `unable to log meeting booked via datalayer`
          });
        }
        break;
      }
    case ActionTypes.SET_EVENT_DATE:
      if (action.date) {
        track('Meetings', 'Set Meeting Property', {
          property: 'event date',
          value: action.date
        });
      }
      break;
    case ActionTypes.SET_EVENT_DATE_TIME:
      if (action.dateTime) {
        track('Meetings', 'Set Meeting Property', {
          property: 'event datetime',
          value: `${action.dateTime}`
        });
      }
      break;
    case ActionTypes.SET_EVENT_DURATION:
      track('Meetings', 'Set Meeting Property', {
        property: 'event duration',
        value: `${action.duration}`
      });
      break;
    case ActionTypes.SET_TIMEZONE:
      track('Meetings', 'Set Meeting Property', {
        property: 'timezone',
        value: action.timezone
      });
      break;
    case ActionTypes.TOGGLE_TROUBLESHOOT_MODE:
      track('Meetings', 'Troubleshoot', {
        action: action.active ? 'enter' : 'exit'
      });
      break;
    case ActionTypes.MEETING_CANCEL_SUCCEEDED:
      track('Meetings', 'Meeting Event Interaction', {
        action: 'clicked cancel',
        source: meetingSource
      });
      break;
    case ActionTypes.MEETING_RESCHEDULE_SUCCEEDED:
      track('Meetings', 'Meeting Event Interaction', {
        action: 'clicked reschedule',
        source: meetingSource
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKED_SUCCESSFULLY:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'book paid meeting success'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_COMPLETED_PROPERTY_FORM:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'completed property form'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_COMPLETED_LEGAL_CONSENT_FORM:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'completed legal consent form'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_SELECTED_TIME:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'selected time'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_PAYMENT_PROCESSING:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'payment processing'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_PAYMENT_SUCCESSFUL:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'payment successful'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_PAYMENT_FAILED:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'payment failed'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_TIME_UNAVAILABLE:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'paid meeting booked time unavailable'
      });
      break;
    case ActionTypes.PAID_MEETING_BOOKING_FAILURE:
      track('Meetings', 'Payment Buyer Interaction', {
        step: 'book paid meeting failure'
      });
      break;
    case ActionTypes.PUBLIC_AVAILABILITY_INFO_FETCH_SUCCEEDED:
      {
        track('Meetings', 'Pageview', {
          subscreen: 'booking',
          stage: 'public availability info fetch succeeded',
          referrer: trackingParams['referrer'] === 'FORMS' ? 'forms' : 'none'
        });
        break;
      }
    case ActionTypes.MEETING_CREATE_SUCCEEDED:
      {
        track('Meetings', 'Pageview', {
          subscreen: 'confirmation',
          stage: 'meeting create succeeded'
        });
        break;
      }
    default:
  }
});